import Link from 'next/link';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { IconButton, Stack, styled, Typography } from '@/primitives';
import { footerMessages } from './messages';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { FOOTER_HEIGHT, MOBILE_FOOTER_HEIGHT } from '@/configs/layout';
import { HealthStatusChip } from './HealthStatusChip';
import ColorSchemeToggle from './toolbar/ColorSchemeToggle';

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 26px;
  padding: 2px 4px;
  margin-top: 0 !important;
  text-transform: capitalize;
`;

interface ShellFooterProps {
  /** When true, footer is fixed to the viewport bottom and does not scroll with the page */
  fixed?: boolean;
}

export default function ShellFooter({ fixed = false }: ShellFooterProps) {
  return (
    <Stack
      component="footer"
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: { xs: '16px', md: '24px' },
        borderTop: 1,
        borderColor: 'divider',
        minHeight: { xs: MOBILE_FOOTER_HEIGHT, md: FOOTER_HEIGHT },
        bgcolor: 'background.paper',
        zIndex: 1300,
        ...(fixed && {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }),

        '@media print': {
          display: 'none',
        },
      }}
    >
      <Stack direction="row" gap={{ xs: 0, sm: '12px', md: '24px' }}>
        <StyledLink href={PUBLIC_ROUTES.HOME}>
          <Typography
            color="text.secondary"
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
            }}
          >
            © {new Date().getFullYear()} ABRI
          </Typography>
        </StyledLink>
        <StyledLink
          href={PUBLIC_ROUTES.TERMS}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Typography
            color="text.secondary"
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
            }}
          >
            {footerMessages['footer.terms.label']}
          </Typography>
        </StyledLink>
        <StyledLink
          href={PUBLIC_ROUTES.PRIVACY}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Typography
            color="text.secondary"
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
            }}
          >
            {footerMessages['footer.privacy.label']}
          </Typography>
        </StyledLink>
      </Stack>
      <Stack alignItems="center" direction="row" gap="8px">
        <HealthStatusChip />
        <ColorSchemeToggle layout="footer" />
        <StyledLink href={PUBLIC_ROUTES.SUPPORT} target="_blank">
          <Typography
            color="text.primary"
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: { xs: 400, sm: 500 },
              letterSpacing: { xs: '0.1px', sm: '0.5px' },
              whiteSpace: 'nowrap',
            }}
          >
            {footerMessages['footer.support.label']}
          </Typography>
        </StyledLink>
        <IconButton
          sx={{ height: '14px', width: '14px', padding: '2px' }}
          onClick={() => {
            document
              .getElementById('main')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
            document
              .getElementById('shell')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <ArrowDropUpIcon
            sx={{
              color: 'text.primary',
              fontSize: '12px',
            }}
          />
        </IconButton>
      </Stack>
    </Stack>
  );
}
